import GQSettings from 'components/GQSettings/GQSettings';
import SelectCountry from 'components/Homepage/SelectCountry';
import CountryGraphSelectCountry from 'components/Homepage/CountryGraphSelectCountry';
import { RiskView, RiskViewPage } from 'components/Homepage/RiskView';
import { SortByRisk, SortByRiskPage } from 'components/Homepage/SortByRisk';
import ScoreEventProvider from 'components/scoringPage/ScoreEventProvider';
import { DEFAULT_VIEWS, VIEW } from 'interfaces';
import React from 'react';
import { Route, Routes, useParams, Navigate } from 'react-router-dom';
import InsightTemplateHtmlDaily from 'components/GQTemplate/InsightTemplate/InsightTemplateHtml/InsightTemplateHtmlDaily';
import InsightTemplatePdf from 'components/GQTemplate/InsightTemplate/InsightTemplatePdf/InsightTemplatePdf';
import InsightTemplateHtmlWeekly from 'components/GQTemplate/InsightTemplate/InsightTemplateHtml/InsightTemplateHtmlWeekly';
import SignalsTemplateDaily from 'components/GQTemplate/SignalsTemplate/SignalsTemplateDaily';
import SignalsTemplateWeekly from 'components/GQTemplate/SignalsTemplate/SignalsTemplateWeekly';
import SignalsTemplateMonthly from 'components/GQTemplate/SignalsTemplate/SignalsTemplateMonthly';
import Activate from './components/Activate';
import GQTemplate from 'components/GQTemplate/GQTemplate';
import GQLoader from 'components/GQLoader/GQLoader';
import Logo from 'assets/general/Logo_BMI_Geo_Risk_Signals_White.svg';
import { CreateInsightContainer } from 'components/GQActivityPage/Insights/CreateInsight';
import { SortByDelta, SortByDeltaPage } from 'components/Homepage/SortByDelta';
import { WorldGraph, WorldGraphPage } from 'components/Homepage/WorldGraph';
import { Heatmap, HeatmapPage } from 'components/Homepage/Heatmap';
import {
    CountryGraph,
    CountryGraphPage,
} from 'components/Homepage/CountryGraph';
import {
    ContingentMap,
    ContingentMapPage,
} from 'components/Homepage/ContingentMap';
import {
    ContingentGraph,
    ContingentGraphPage,
} from 'components/Homepage/ContingentGraph';
import { Prompt } from 'components/Homepage/Prompt';
import { SUPPORT_EMAIL } from 'services/configService';
import Impersonate from 'components/Impersonate';
import useInit from 'hooks/useInit';
import Unsub from 'components/Unsub';
import {
    SortByContingentRisk,
    SortByContingentRiskPage,
} from 'components/Homepage/SortByContingentRisk';
import {
    SortByContingentDelta,
    SortByContingentDeltaPage,
} from 'components/Homepage/SortByContingentDelta';
import Layout from 'Layout';
import Methodologies from 'components/Homepage/Methodologies';
import { usePingAuth } from 'hooks/usePingAuth';
import { FGTypography } from '@fg/design-system';
import EventsDisplayProvider from 'components/eventsFeed/EventDisplayProvider';
import InsightDisplayProvider from 'components/GQActivityPage/activityPage/InsightDisplayProvider';
import PredictionDisplayProvider from 'components/GQActivityPage/activityPage/PredictionDisplayProvider';
import RiskIntelligence from 'components/GQActivityPage/activityPage/RiskIntelligence';
import { isProdQAWebApp } from './utils/generalUtils';
import ExportChartTemplate from 'components/GQTemplateV2/ExportChartTemplate';
import PredictionTemplatePdf from 'components/GQTemplate/PredictionTemplate/PredictionTemplatePdf';
import NoPermissions from 'components/NoPermissions';

const Router = React.memo(() => {
    return (
        <Routes>
            <Route path="/impersonate/:token" element={<Impersonate />} />
            <Route path="/unsub/:type/:uid" element={<Unsub />} />
            <Route path="/activate/:token" element={<Activate />} />
            <Route path="/*" element={<LoggedInRoutes />} />
        </Routes>
    );
});

const LoggedInRoutes = React.memo(() => {
    const { didPing, hasGQPermissions, hasQAPermissions } = usePingAuth();
    if (!didPing) {
        return (
            <div
                style={{
                    display: 'flex',
                    height: '100vh',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}>
                <GQLoader
                    style={{
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                    }}
                />
            </div>
        );
    }
    return (
        <Routes>
            <Route path="export/*" element={<ExportRoutes />} />
            <Route
                path={`/${VIEW.EXPORT_COMPONENT}/:id?`}
                element={<ExportComponentRoute />}
            />
            <Route
                path={`${VIEW.EXPORT_CHART}`}
                element={<ExportChartRoute />}
            />
            <Route path="/unsub/:type/:uid" element={<Unsub />} />
            {/*todo: delete after deployment of fitch component library has been successful*/}
            <Route
                path="*"
                element={
                    <RoutesWithPageContainer
                        hasGQPermissions={hasGQPermissions}
                        hasQAPermissions={hasQAPermissions}
                    />
                }
            />
        </Routes>
    );
});

const RoutesWithPageContainer = React.memo(
    ({
        hasGQPermissions,
        hasQAPermissions,
    }: {
        hasGQPermissions: boolean;
        hasQAPermissions: boolean;
    }) => {
        const { didInitStores, preloadError } = useInit({ hasGQPermissions });

        if (preloadError) {
            return (
                <div className="app">
                    <div className="preload-error">
                        <div className="preload-error-logo">
                            <img src={Logo} alt="" />
                        </div>
                        <div>A network error has occured.</div>
                        <div>Please try again in a few min.</div>
                        <div>
                            If the error persists, please contact us at{' '}
                            {SUPPORT_EMAIL}
                        </div>
                    </div>
                </div>
            );
        }

        if (!didInitStores) {
            return (
                <div
                    style={{
                        height: '100vh',
                        display: 'flex',
                        alignItems: 'center',
                    }}>
                    <GQLoader
                        style={{
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    />
                </div>
            );
        }
        if (!hasQAPermissions && isProdQAWebApp()) {
            return (
                <Layout>
                    <NoPermissions>
                        <FGTypography tag="p">
                            You do not have access to BMI Geo Risk Signals' QA
                            platform. Please use{' '}
                            <a
                                href="https://app.bmigeorisksignals.com"
                                target="_blank"
                                rel="noopener noreferrer">
                                app.bmigeorisksignals.com
                            </a>{' '}
                            instead.
                        </FGTypography>
                    </NoPermissions>
                </Layout>
            );
        }
        if (!hasGQPermissions) {
            return (
                <Layout>
                    <NoPermissions>
                        <div>
                            You do not have access to BMI Geo Risk Signals.
                        </div>
                    </NoPermissions>
                </Layout>
            );
        }

        return (
            <Layout>
                <Routes>
                    <Route
                        path="*"
                        element={
                            <Navigate to={`/${DEFAULT_VIEWS.HOME}`} replace />
                        }
                    />
                    <Route
                        path={`/${VIEW.HEATMAP}`}
                        element={<HeatmapPage />}
                    />
                    <Route
                        path={`/${VIEW.SCORE_EVENT}/:id?`}
                        element={<ScoreEventProvider />}
                    />
                    <Route
                        element={<RiskViewPage />}
                        path={`/${VIEW.RISK_VIEW}`}
                    />
                    <Route element={<Prompt />} path={`/${VIEW.PROMPT}`} />
                    <Route
                        element={<SortByRiskPage />}
                        path={`/${VIEW.SORT_BY_RISK}`}
                    />
                    <Route
                        element={<SortByDeltaPage />}
                        path={`/${VIEW.SORT_BY_DELTA}`}
                    />
                    <Route
                        element={<SortByContingentRiskPage />}
                        path={`/${VIEW.SORT_BY_CONTINGENT_RISK}`}
                    />
                    <Route
                        element={<SortByContingentDeltaPage />}
                        path={`/${VIEW.SORT_BY_CONTINGENT_DELTA}`}
                    />
                    <Route
                        element={<WorldGraphPage />}
                        path={`/${VIEW.WORLD_GRAPH}`}
                    />
                    <Route
                        element={<CountryGraphPage />}
                        path={`/${VIEW.COUNTRY_GRAPH}`}
                    />
                    <Route
                        path={`/${VIEW.METHODOLOGIES}`}
                        element={<Methodologies />}
                    />
                    <Route
                        element={<ContingentMapPage />}
                        path={`/${VIEW.CONTINGENT}`}
                    />
                    <Route
                        path={`/${VIEW.COUNTRY_GRAPH_SELECT_COUNTRY}`}
                        element={<CountryGraphSelectCountry />}
                    />
                    <Route
                        path={`/${VIEW.SELECT_COUNTRY}/:nextView?`}
                        element={<SelectCountry />}
                    />
                    <Route
                        element={<ContingentGraphPage />}
                        path={`/${VIEW.CONTINGENT_GRAPH}`}
                    />
                    <Route
                        element={<EventsDisplayProvider />}
                        path={`${VIEW.EVENT_DISPLAY}/:id?`}
                    />
                    <Route
                        element={<RiskIntelligence />}
                        path={`${VIEW.RISK_INTELLIGENCE}/*`}
                    />
                    <Route
                        element={<InsightDisplayProvider />}
                        path={`${VIEW.INSIGHT_DISPLAY}/:id?`}
                    />
                    <Route
                        element={<PredictionDisplayProvider />}
                        path={`${VIEW.PREDICTION_DISPLAY}/:id?`}
                    />
                    <Route
                        path={`/${VIEW.CREATE_INSIGHT}/*`}
                        element={<CreateInsightContainer />}>
                        <Route
                            element={<RiskView />}
                            path={`${VIEW.RISK_VIEW}/:id?`}
                        />
                        <Route
                            element={<SortByRisk />}
                            path={`${VIEW.SORT_BY_RISK}/:id?`}
                        />
                        <Route
                            element={<SortByContingentRisk />}
                            path={`${VIEW.SORT_BY_CONTINGENT_RISK}/:id?`}
                        />
                        <Route
                            element={<SortByDelta />}
                            path={`${VIEW.SORT_BY_DELTA}/:id?`}
                        />
                        <Route
                            element={<SortByContingentDelta />}
                            path={`${VIEW.SORT_BY_CONTINGENT_DELTA}/:id?`}
                        />
                        <Route
                            element={<WorldGraph />}
                            path={`${VIEW.WORLD_GRAPH}/:id?`}
                        />
                        <Route
                            element={<CountryGraph />}
                            path={`${VIEW.COUNTRY_GRAPH}/:id?`}
                        />
                        <Route
                            element={<ContingentMap />}
                            path={`${VIEW.CONTINGENT}/:id?`}
                        />
                        <Route
                            element={<ContingentGraph />}
                            path={`${VIEW.CONTINGENT_GRAPH}/:id?`}
                        />
                        <Route
                            element={<Heatmap />}
                            path={`${VIEW.HEATMAP}/:id?`}
                        />
                    </Route>
                    <Route
                        path={`/${VIEW.SETTINGS}/:tab?`}
                        element={<GQSettings />}
                    />
                    <Route
                        path="/impersonate/:token"
                        element={<Impersonate />}
                    />
                </Routes>
            </Layout>
        );
    }
);

const ExportComponentRoute = React.memo(() => {
    const { ready } = useInit();
    const { id } = useParams();
    if (!ready) {
        return null;
    }
    return <GQTemplate id={id} />;
});

const ExportRoutes = React.memo(() => {
    return (
        <Routes>
            <Route
                path={`${VIEW.SIGNALS_DAILY}`}
                element={<SignalsTemplateDaily />}
            />
            <Route
                path={`${VIEW.SIGNALS_WEEKLY}`}
                element={<SignalsTemplateWeekly />}
            />
            <Route
                path={`${VIEW.SIGNALS_MONTHLY}`}
                element={<SignalsTemplateMonthly />}
            />
            <Route
                path={`${VIEW.INSIGHT_HTML_DAILY}/:id?`}
                element={<InsightTemplateHtmlDaily />}
            />
            <Route
                path={`${VIEW.INSIGHT_HTML_WEEKLY}/:id?`}
                element={<InsightTemplateHtmlWeekly />}
            />
            <Route
                path={`${VIEW.INSIGHT_PDF}/:id?`}
                element={<InsightTemplatePdf />}
            />
            <Route
                path={`${VIEW.PREDICTION_EXPORT}/:id?`}
                element={<PredictionTemplatePdf />}
            />
        </Routes>
    );
});

const ExportChartRoute = React.memo(() => {
    /* 
    Endpoint used by headless export to take chart screenshots.
    The chart config (JSON) is passes as a prop in the query params.
    */
    const hash = window.location.hash; // use location hash to get the part after the #
    const hashQuery = hash.split('?')[1] || '';
    const queryParams = new URLSearchParams(hashQuery);
    const encodedState = queryParams.get('chartState');
    const decodedState = JSON.parse(encodedState);
    return <ExportChartTemplate chartConfig={decodedState as any} />;
});

export default Router;
